import { useCallback } from "react";
import { useAtomValue } from "jotai";
import { shipToLocationIdAtom } from "@/store/shipToAtoms";
import { useApi, type ApiRequestOptions } from "./useApi";

// The ONE place `shipToLocationId` is spelled (#228 / E4-S12). Fourteen storefront reads carry
// it — nine through the per-resource api hooks, four raw in feature code, plus the three
// /products/{id} image hydrations — and fourteen call sites that each append their own query
// parameter is fourteen places for it to drift out of. buildSearchUrl is deliberately NOT given
// a copy: two owners is the failure this is here to prevent.
export const SHIP_TO_PARAM = "shipToLocationId";

// Pure, so the append rule is testable without a hook. A null id appends NOTHING rather than an
// empty value: absent means "the API picks the caller's first eligible location", while
// `shipToLocationId=` is unparseable and answers 400 on every read at once.
export const withShipTo = (url: string, locationId: string | null): string => {
    if (!locationId) {
        return url;
    }

    const separator = url.includes("?") ? "&" : "?";

    return `${url}${separator}${SHIP_TO_PARAM}=${encodeURIComponent(locationId)}`;
};

// callApi with the active destination attached. Latest-wins on a switch comes free from the
// identity: the id is in the useCallback deps, so switching location hands every consumer a new
// `callShipToApi`, which re-issues each read whose effect keys on it. The reads that publish to
// MODULE-scope atoms need more than that — a superseded read still writes them from its own
// catch/finally — so those carry a request-sequence guard of their own; see useProductApi.
export const useShipToApi = () => {
    const { callApi } = useApi();
    const shipToLocationId = useAtomValue(shipToLocationIdAtom);

    const callShipToApi = useCallback(
        <T = unknown>(
            endpoint: string,
            // The JSON form only: ship-to reads parse a body, none downloads.
            options: ApiRequestOptions & { responseType?: "json" } = {},
        ): Promise<T> =>
            callApi<T>(withShipTo(endpoint, shipToLocationId), options),
        [callApi, shipToLocationId],
    );

    return { callShipToApi, shipToLocationId };
};
